import { selfServePlanBadge } from './services/planBadge.js';
import { takeTemplateAuthReturn } from './core/templateEntry.js';
import { inPortfolio } from './services/portfolio-view.js';
import { clientLanding } from './core/performanceNavigation.js';
/**
 * StreamSyncEngage SaaS Client - Main Entry Point
 *
 * Initializes Navigo router, mvvLegit authentication, and pub/sub event system.
 *
 * NOTE: mvvLegit is provided globally by Neodigm 55 library (loaded in index.html)
 * It handles:
 * - JWT token management and automatic injection into fetch requests
 * - Route protection and navigation control
 * - Authentication state persistence in compressed localStorage
 */

// Import app styles: Tailwind v4 (utilities + theme) + shared tokens/reset/launCh
import './styles.css';
import './styles/dashboard-kit.css';
import './styles/ui-kit.css';
import './styles/settings-kit.css';
import './styles/workspace-shell.css';
import { mountWorkspaceHeader, clientContextMarkup } from './core/workspaceShell.js';

import Navigo from 'navigo';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../shared/appBroadcast.js';
import { initAppEvents } from './core/app_events.js';
import {readSigninAttempt} from './services/shopify/signin-flow.js';
import { publicLandingFor } from './core/publicLanding.js';
import AuthController from './services/AuthController.js';
import { I18nController } from './controllers/i18nController.js';
import MachVive from '../../../shared/machvive.js';
import '../../../shared/wc/m5t-side-nav.js';
import '../../../shared/wc/m5t-primary-nav.js';
import { resetScrollForRoute } from './core/routeScroll.js';
import { NavMetaManager } from '../../../shared/meta/meta_nav.js';
import { installEditorNavigationGuard, allowEditorNavigation, rememberEditorReturn, takeEditorReturn } from './core/magnetEditorSession.js';
import { requireRouteCapability } from './core/routeCapability.js';
import { registerMagnetWorkspaceRoutes } from './core/magnetWorkspaceRouter.js';
import { registerMagnetOverviewRoutes } from './core/magnetOverviewRouter.js';
import { magnetOverviewEnabled, rememberMagnetHomeReturn, takeMagnetHomeReturn, validHomePath } from './core/magnetOverview.js';
import { rememberLeadBootRoute, takeLeadBootRoute } from './core/leadWorkspaceMemory.js';
import { rememberShopifyBootRoute, takeShopifyBootRoute, clearShopifyBootRoute, retainShopifyAuthReturn } from './services/shopify/boot-route.js';
import { navigateFromSideNav } from './core/sideNavNavigation.js';
import { portfolioLabel } from './core/portfolioLabel.js';
import { FEATURE_FLAGS } from '../../../shared/featureFlags.js';
import { mountNotificationBell, setNotificationBellVisible } from './core/notificationBell.js';
import { darkModeEnabled, animationsDisabled, applyDarkModeButtonVisibility } from './core/uiFlagGates.js';
import { initTheme, toggleTheme } from './core/theme.js';

// Initialize m5t namespace for shared components
window.m5t = Object.create(null);
window.m5t.pubsub = PubSub;
// Build identity compiled in by vite.config.js `define` (see there). Read-only diagnostics.
window.m5t.version = __M5M_VERSION__;
window.m5t.buildTime = __M5M_BUILD_TIME__;

// mvvLegit is available globally from Neodigm 55 library
// Access it via window.mvvLegit

console.log('StreamSyncEngage SaaS Client - Starting...');
console.log('Environment:', import.meta.env.MODE);
console.log('Document readyState:', document.readyState);

// Debug: Check all possible ways to access mvvLegit
console.log('window.mvvLegit:', typeof window.mvvLegit);
console.log('globalThis.mvvLegit:', typeof globalThis.mvvLegit);
try {
  console.log('mvvLegit (direct):', typeof mvvLegit);
} catch (e) {
  console.log('mvvLegit (direct): ReferenceError -', e.message);
}

// List all globals that might be related
const possibleGlobals = Object.keys(window).filter(k =>
  k.toLowerCase().includes('mvv') ||
  k.toLowerCase().includes('neodigm') ||
  k.toLowerCase().includes('legit')
);
console.log('Possible Neodigm globals:', possibleGlobals);

// ============================================
// Router Configuration
// ============================================

const router = new Navigo('/', { hash: true });

// Expose router globally for use in event handlers and route modules
window.router = router;
installEditorNavigationGuard(router);
rememberEditorReturn(window.location.hash);
rememberMagnetHomeReturn(window.location.hash);
rememberLeadBootRoute(window.location.hash);
rememberShopifyBootRoute(window.location.hash);
window.addEventListener('hashchange', () => retainShopifyAuthReturn(window.location.hash));

/**
 * Check route access before navigation
 * Converts URL path to route token format expected by mvvLegit
 */
function checkRouteAccess(routePath) {
  // Convert path to route token (replace hyphens with underscores and add _route suffix)
  const routeToken = routePath ? `${routePath.replace(/-/g, '_')}_route` : 'splash_route';

  console.log(`[Router] Checking access: ${routePath} -> ${routeToken}`);

  // Check with mvvLegit
  const isAllowed = mvvLegit.isRouteAllowed(routeToken);

  if (!isAllowed) {
    console.warn(`[Router] Access denied to route: ${routeToken}`);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Please sign in to access this page',
      type: 'warning'
    });
    return false;
  }

  return true;
}

/**
 * Navigate to route by name
 */
function navigateToRoute(routeName) {
  console.log('[Router] Navigating to:', routeName);
  router.navigate(`/${routeName}`);
}

// ============================================
// Side Navigation (persona-aware left nav)
// ============================================

// Full-screen auth/marketing routes — the side nav stays hidden on these.
const SIDE_NAV_HIDDEN_ROUTES = new Set([
  'splash', 'signin', 'signup', 'forgot', 'resethash', 'verf-link', 'accept-invite', 'signout'
]);

// Route path ('/analytics/conversions') -> { sectionToken, childToken, children }
// for left-nav active state + the contextual top sub-nav.
let sideNavRouteIndex = {};
// sub-nav child token -> route, for navigating from a sub-nav tap.
let subNavRouteByToken = {};
let currentRouteName = null;

// Persona badge shown in the nav footer, keyed by uiMode.
const PERSONA_BADGE = {
  admin:      { caption: 'Platform Admin', icon: 'shield_person' },
  reseller:   { caption: 'Reseller',       icon: 'handshake' },
  managed:    { caption: 'Managed',        icon: 'visibility' },
  self_serve: { caption: 'Self-Serve',     icon: 'rocket_launch' },
};

// Plan reads belong to the authenticated workspace, not the current route.
function selfServeBadge() { return selfServePlanBadge(renderSideNavFromScope); }

// Org-switcher sentinel: a reseller's default scope (no explicit org = every
// client unioned + the per-client breakdown on the dashboard).
const ORG_PORTFOLIO = '__portfolio__';

/** Re-run the current route's handler (data routes re-fetch under the new org context). */
function reloadCurrentRoute() {
  try { router._setCurrent(null); } catch { /* Navigo internal — best effort */ }
  router.resolve();
}

/** Render the side nav from the current AuthController scope (capabilities + uiMode + orgs). */
function renderSideNavFromScope() {
  const nav = document.getElementById('m5t-side-nav');
  if (!nav) return;

  // Cold sign-in: authenticated but /me not back yet → show a skeleton instead of
  // the ungated (Settings-only) tree, which would otherwise flash. Returning users
  // have cached scope and paint the real tree immediately.
  const loading = !AuthController.getScope() && AuthController.isAuthenticated();

  const caps = AuthController.getCapabilities();
  const uiMode = AuthController.getUiMode();
  const tree = NavMetaManager.getSideNav(caps, { uiMode, portfolio: inPortfolio() });

  // Rebuild the route index (section + children) and the child route map.
  sideNavRouteIndex = {};
  subNavRouteByToken = {};
  const indexItem = (item) => {
    if (!item.route) return;
    sideNavRouteIndex[item.route] = { sectionToken: item.token, childToken: null, children: item.children || null };
    if (Array.isArray(item.children)) {
      item.children.forEach((c) => {
        if (!c.route) return;
        subNavRouteByToken[c.token] = c.route;
        // A child route resolves to its parent section (left active) + itself (sub active).
        // A shared parent/child route (e.g. /analytics == Overview) makes the first child active.
        sideNavRouteIndex[c.route] = { sectionToken: item.token, childToken: c.token, children: item.children };
      });
    }
  };
  const walk = (nodes) => nodes.forEach((n) => {
    if (n.group && Array.isArray(n.items)) walk(n.items);
    else indexItem(n);
  });
  walk(tree);
  const accountChildren = NavMetaManager.getAccountNav();
  indexItem({ token: 'ACCOUNT', route: '/settings', children: accountChildren });

  const memberships = AuthController.getMemberships();
  const activeOrg = AuthController.getActiveOrg();
  const clients = AuthController.getManagedClients();
  const storedOrg = AuthController.getStoredActiveOrg();

  // Reseller book: Portfolio (default, no explicit context) + own org(s) + every
  // managed client, grouped. Everyone else: plain membership list (hidden when 1).
  const orgEntries = clients.length
    ? [
        { guid: ORG_PORTFOLIO, name: portfolioLabel(AuthController.isClientScopeRestricted?.()) },
        ...memberships.map((m) => ({ guid: m.orgGuid, name: m.name || m.channel, channel: m.channel, group: 'Your organization' })),
        ...clients.map((c) => ({ guid: c.orgGuid, name: c.name || c.orgGuid, channel: c.channel, group: 'Clients' })),
      ]
    : memberships.map((m) => ({ guid: m.orgGuid, name: m.name || m.channel, channel: m.channel }));

  // One batched render: nav tree + org switcher + persona badge.
  nav.setState({
    buttons: tree,
    orgs: orgEntries,
    activeOrg: clients.length ? (storedOrg || ORG_PORTFOLIO) : (activeOrg?.guid || null),
    footerBadge: uiMode === 'self_serve' ? selfServeBadge() : (PERSONA_BADGE[uiMode] || null),
    loading,
  });

  renderOrgContextBanner();

  // Re-apply active section + sub-nav for the current route (setState re-rendered the tree).
  if (currentRouteName) applyActiveForRoute(currentRouteName);
}

// ============================================
// Org context banner ("Viewing client" / preview-as-client)
// ============================================

/**
 * A persistent strip above the content whenever the reseller is acting inside a
 * client's context, so it is always unmistakable whose numbers are on screen.
 * Also hosts the Preview-as-client toggle (pixel-true managed-client rendering
 * for presale demos / status calls) and the way back to the portfolio.
 */
function renderOrgContextBanner() {
  const content = document.getElementById('app-content');
  if (!content) return;
  let el = document.getElementById('org-context-banner');
  if (!el) {
    el = document.createElement('div');
    el.id = 'org-context-banner';
    content.insertBefore(el, content.firstChild);
  }

  const active = AuthController.getActiveOrg();
  const previewing = AuthController.isPreviewingAsClient();
  const show = !!active?.viaSubtree && !!currentRouteName && !SIDE_NAV_HIDDEN_ROUTES.has(currentRouteName);
  el.hidden = !show;
  if (!show) { el.innerHTML = ''; return; }

  el.innerHTML = clientContextMarkup(active.name || 'Client', previewing);

  el.querySelector('[data-ocb="preview"]')?.addEventListener('click', () => {
    AuthController.setPreviewAsClient(!AuthController.isPreviewingAsClient());
    // The raw scope (and its signature) is unchanged — reload explicitly so the
    // route renders against the masked capabilities.
    reloadCurrentRoute();
  });
  el.querySelector('[data-ocb="portfolio"]')?.addEventListener('click', () => {
    AuthController.setPreviewAsClient(false);
    AuthController.setActiveOrg(null); // scope-signature subscription re-runs the route
  });
}

/** Set the active left-nav section + feed/show the contextual top sub-nav for a route. */
function applyActiveForRoute(routeName) {
  const nav = document.getElementById('m5t-side-nav');
  const uiMode = AuthController.getUiMode();
  nav?.setFooterBadge(uiMode === 'self_serve' ? selfServeBadge() : (PERSONA_BADGE[uiMode] || null));
  const entry = sideNavRouteIndex[`/${routeName}`] || sideNavRouteIndex[routeName] || null;
  nav?.setActiveToken(entry?.sectionToken || null, entry?.childToken || null);
  updateSubNav(entry);
}

/** Contextual top sub-nav: shown only when the active section has >1 sub-item. */
function updateSubNav(entry) {
  const sub = document.getElementById('m5t-sub-nav');
  const shell = document.getElementById('app-shell');
  if (!sub) return;

  // VISIBILITY BELONGS TO THE ROUTE, and is re-derived here rather than trusted from the
  // caller. updateSideNavForRoute() hides this bar on auth/splash routes, but it is not
  // the only thing that reaches updateSubNav(): renderSideNavFromScope() ends with
  // `applyActiveForRoute(currentRouteName)` and runs again on AUTH.SCOPE.LOADED and on
  // language-changed. Both call in with whatever route is current — including a hidden
  // one — and the unconditional `sub.hidden = false` below then put the bar back on
  // screen after the route had correctly removed it.
  //
  // That is why every piece looked right in isolation: the route handler DOES hide it,
  // and it stays hidden for a signed-OUT visitor because no scope ever loads. Reload the
  // app while signed in and /me resolves a moment after splash paints, which re-renders
  // the nav and un-hides this bar over the splash screen. Confirmed in a browser against
  // production: publishing AUTH.SCOPE.LOADED on a hidden route flipped it from
  // hidden/display:none to visible/61px.
  if (SIDE_NAV_HIDDEN_ROUTES.has(currentRouteName) || validHomePath(location.hash.slice(1))) {
    sub.hidden = true;
    shell?.classList.remove('has-sub-nav');
    return;
  }

  const children = entry?.children;
  // Pages without local navigation use the space for their content.
  if (children && children.length > 1) {
    sub.hidden = false;
    sub.updateButtons(children.map((c) => ({
      token: c.token, caption: c.caption, enabled: true, selected: c.token === entry.childToken,
    })));
    shell?.classList.add('has-sub-nav');
  } else {
    sub.hidden = true;
    sub.updateButtons([]);
    shell?.classList.remove('has-sub-nav');
  }
}

/** Cycle to the next available UI language. */
function cycleLanguage() {
  const langs = (I18nController.getAvailableLanguages && I18nController.getAvailableLanguages()) || [];
  if (langs.length < 2) {
    PubSub.publish(AppBroadcast.UI_TOAST, { message: 'Only one language is available', type: 'info' });
    return;
  }
  const current = localStorage.getItem(FEATURE_FLAGS.FF_SSE_LS_USER_LANG) || FEATURE_FLAGS.FF_SSE_I18N_LANG || langs[0];
  const next = langs[(langs.indexOf(current) + 1) % langs.length];
  I18nController.setLanguage(next);
  PubSub.publish(AppBroadcast.UI_TOAST, { message: `Language: ${next}`, type: 'info' });
}

// Theme control moved to core/theme.js so Settings (General) can render a real
// switch alongside the nav-footer toggle; policy (light-first, kill-switch
// aware, explicit-action-only) is unchanged and documented there.

/** Suppress animations/transitions app-wide when FF_SSE_UI_ANIMATIONS is off,
 *  via the ff-no-animations class enforced in shared/styles/index.css. Runs at boot. */
function applyAnimationsPref() {
  document.documentElement.classList.toggle('ff-no-animations', animationsDisabled());
}

/** Feed + wire the side nav once, then keep it in sync with scope. Runs before router.resolve(). */
function setupSideNav() {
  const nav = document.getElementById('m5t-side-nav');
  if (!nav) return;
  NavMetaManager.setAppToken('client-saas');
  mountWorkspaceHeader({
    onHome: () => router.navigate('/home'),
    onAccount: () => router.navigate('/settings'),
    onSignout: () => router.navigate('/signout'),
    onTheme: toggleTheme,
    showTheme: darkModeEnabled(),
  });

  // FF_SSE_UI_DARK_MODE kill switch: hide the toggle button entirely when off
  // (toggleTheme() itself also no-ops as a guard against direct invocation).
  applyDarkModeButtonVisibility(nav);

  // Sync the shell's collapsed class with the nav's RESTORED collapse state (from
  // localStorage). The component restores data-collapsed on connect without firing
  // nav-collapse, so without this the content uses the expanded margin against a
  // collapsed rail on first paint (a big gap) until the user toggles the menu.
  document.getElementById('app-shell')?.classList.toggle(
    'nav-collapsed', nav.getAttribute('data-collapsed') === 'true'
  );

  // Component emits composed CustomEvents; route everything through the router/controllers.
  nav.addEventListener('nav-select', (e) => {
    navigateFromSideNav(e.detail?.route, router, reloadCurrentRoute);
  });
  nav.addEventListener('logo-click', () => router.navigate('/home'));
  nav.addEventListener('nav-collapse', (e) => {
    document.getElementById('app-shell')?.classList.toggle('nav-collapsed', !!e.detail?.collapsed);
  });
  nav.addEventListener('org-switch', (e) => {
    const guid = e.detail?.orgGuid;
    if (!guid) return;
    // Portfolio = no explicit context (reseller default scope). Persist + refetch
    // /me → AUTH.SCOPE.LOADED → the scope-signature subscription re-runs the
    // current route, whose data re-fetches under the new X-Active-Org context.
    if (!allowEditorNavigation('/magnets', { forceLeave: true })) { renderSideNavFromScope(); return; }
    AuthController.setActiveOrg(guid === ORG_PORTFOLIO ? null : guid);
  });
  nav.addEventListener('footer-action', (e) => {
    switch (e.detail?.action) {
      case 'account': router.navigate('/settings'); break;
      case 'signout': router.navigate('/signout'); break;
      case 'lang':    cycleLanguage(); break;
      case 'theme':   toggleTheme(); break;
    }
  });

  // Contextual top sub-nav (m5t-primary-nav reused): tap a sub-item → navigate to its route.
  const sub = document.getElementById('m5t-sub-nav');
  sub?.addEventListener('nav-select', (e) => {
    const route = subNavRouteByToken[e.detail?.token];
    if (route) router.navigate(route);
  });

  // Re-resolve nav i18n captions on language change (I18nController fires this).
  document.addEventListener('language-changed', () => renderSideNavFromScope());

  // Initial render from cached/empty scope; re-render whenever scope resolves/changes.
  renderSideNavFromScope();
  PubSub.subscribe(AppBroadcast.AUTH_SCOPE_LOADED, () => renderSideNavFromScope());

  // When the scope MATERIALLY changes (org context, capabilities, clients list —
  // e.g. /me landing after a cold sign-in, or an org switch initiated elsewhere),
  // re-run the current route so persona-dependent views (portfolio dashboard,
  // capability-gated buttons) render against the new scope. Signature-guarded so
  // routine /me reconciliations never cause a re-render.
  const scopeSig = () => {
    const s = AuthController.getScope();
    // getStoredActiveOrg() belongs in this signature, not just the resolved scope
    // fields: clicking "View" on an org that's already your implicit default (e.g.
    // a reseller viewing their own org) resolves to the exact same uiMode/
    // activeOrg.guid/managedClients/capabilities as before — nothing in those four
    // fields changes — even though the stored explicit-org state DID just flip
    // (null -> a guid), which is what inPortfolio() actually gates on. Without this,
    // that click was a silent no-op: reloadCurrentRoute() never fired because the
    // signature looked unchanged.
    return [s?.uiMode, s?.activeOrg?.guid, (s?.managedClients || []).length,
      (s?.capabilities || []).join(','), AuthController.getStoredActiveOrg()].join('|');
  };
  let lastScopeSig = scopeSig();
  PubSub.subscribe(AppBroadcast.AUTH_SCOPE_LOADED, () => {
    const sig = scopeSig();
    if (sig !== lastScopeSig) {
      lastScopeSig = sig;
      if (currentRouteName && !SIDE_NAV_HIDDEN_ROUTES.has(currentRouteName)) reloadCurrentRoute();
    }
  });
}

/** Show/hide the nav per route and sync the active item (route is source of truth). */
function updateSideNavForRoute(routeName) {
  currentRouteName = routeName;
  const nav = document.getElementById('m5t-side-nav');
  const shell = document.getElementById('app-shell');
  if (!nav || !shell) return;

  const showNav = !SIDE_NAV_HIDDEN_ROUTES.has(routeName);
  nav.hidden = !showNav;
  shell.classList.toggle('has-side-nav', showNav);
  setNotificationBellVisible(showNav);

  if (showNav) {
    applyActiveForRoute(routeName);
  } else {
    nav.closeDrawer();
    // Auth/splash routes: hide the contextual sub-nav too.
    const sub = document.getElementById('m5t-sub-nav');
    if (sub) sub.hidden = true;
    shell.classList.remove('has-sub-nav');
  }

  // Keep the client-context strip in step with route visibility.
  renderOrgContextBanner();
}

// Route is the single source of truth for nav visibility + active item.
PubSub.subscribe(AppBroadcast.ROUTE_NAV_AFTER, (_msg, data) => {
  if (!data?.route) return;
  // Before the nav sync, so the viewport is already at the top when the new page paints.
  // Without this a tab click while scrolled renders the next page underneath the sticky
  // top bar — see core/routeScroll.js for the measurements.
  resetScrollForRoute(data.route);
  updateSideNavForRoute(data.route);
});

// ============================================
// Route Definitions
// ============================================

// Splash is the ONLY unauth landing mvvLegit allows in every auth state (incl. the
// brand-new state 0 of a cold incognito load), so it's where invite links,
// questionnaire fill-out links and deliverable review links are funneled. All
// three ride real query-string params (?uid=&token= / ?qr=&token= / ?dl=&token=),
// which survive the bounce — render the matching form when present.
const loadSplash = () => {
  console.log('[Router] Route: splash');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'splash' });
  // WHICH public landing this is (if any) comes from core/publicLanding.js — the same
  // module AuthController's nav guard consults to decide whether to suppress mvvLegit's
  // redirect to signin. Two independent copies of that condition is how the guard went
  // dead twice; see that file's header.
  const landing = publicLandingFor()?.name;
  // Use LITERAL import() strings in each branch — a computed import(variable) is not
  // statically analyzable by Vite, so the chunk is never emitted/deployed (→ 404 at
  // runtime). That is also why the module path is not stored in the registry next to
  // the name; a test keeps the two in step instead.
  const loader = landing === 'invite'
    ? import('./routes/accept-invite-route.js')
    : landing === 'questionnaire'
      ? import('./routes/questionnaire-fill-route.js')
      : landing === 'deliverable'
        ? import('./routes/deliverable-fill-route.js')
        : landing === 'shopifySignin'
          ? import('./routes/shopify-signin-route.js')
          : import('./routes/splash-route.js');
  loader
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'splash' });
    })
    .catch(err => {
      console.error('[Router] Error loading splash route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'splash', error: err });
    });
};
router.on('/', loadSplash);
router.on('/splash', loadSplash);

router.on('/signin', () => {
  console.log('[Router] Route: /signin');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signin' });

  import('./routes/signin-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signin' });
    })
    .catch(err => {
      console.error('[Router] Error loading signin route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signin', error: err });
    });
});

router.on('/signup', () => {
  console.log('[Router] Route: /signup');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signup' });

  import('./routes/signup-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signup' });
    })
    .catch(err => {
      console.error('[Router] Error loading signup route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signup', error: err });
    });
});

router.on('/verf-link', () => {
  console.log('[Router] Route: /verf-link');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'verf-link' });

  import('./routes/verf-link-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'verf-link' });
    })
    .catch(err => {
      console.error('[Router] Error loading verf-link route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'verf-link', error: err });
    });
});

router.on('/forgot', () => {
  console.log('[Router] Route: /forgot');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'forgot' });

  import('./routes/forgot-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'forgot' });
    })
    .catch(err => {
      console.error('[Router] Error loading forgot route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'forgot', error: err });
    });
});

router.on('/resethash', () => {
  console.log('[Router] Route: /resethash');

  // Check authentication (will convert to resethash_route internally)
  if (!checkRouteAccess('resethash')) {
    navigateToRoute('signin');
    return;
  }

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'resethash' });

  import('./routes/resethash-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'resethash' });
    })
    .catch(err => {
      console.error('[Router] Error loading resethash route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'resethash', error: err });
    });
});

router.on('/home', async () => {
  // Commit the incoming hash before a saved editor destination redirects again.
  await Promise.resolve();
  if (window.location.hash !== '#/home') return;
  console.log('[Router] Route: /home');

  // Check authentication (will convert to home_route internally)
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  const pendingShopifySignin=readSigninAttempt();
  if(pendingShopifySignin?.resume){
    const url=new URL(window.location.href);url.searchParams.set('shopifySignin',pendingShopifySignin.shop);
    url.searchParams.set('shopifyReturn',pendingShopifySignin.destination||'/home');url.hash='/splash';
    window.location.replace(url.href);return;
  }
  const editorReturn = takeEditorReturn();
  const magnetReturn = takeMagnetHomeReturn();
  const leadReturn = takeLeadBootRoute();
  const shopifyReturn = takeShopifyBootRoute();
  if (editorReturn) { router.navigate(editorReturn); return; }
  if (magnetReturn) { router.navigate(magnetReturn); return; }
  if (leadReturn) { router.navigate(leadReturn); return; }
  if (shopifyReturn) { router.navigate(shopifyReturn); return; }
  const templateReturn = takeTemplateAuthReturn();
  if (templateReturn) { router.navigate(templateReturn); return; }
  if (!AuthController.getScope()) await AuthController.fetchScope();
  if (window.location.hash !== '#/home') return;
  if (!AuthController.getScope()) {
    document.getElementById('app').innerHTML = '<div class="page-shell"><p>Could not load your workspace.</p><button id="home-retry">Try again</button></div>';
    document.getElementById('home-retry').addEventListener('click', reloadCurrentRoute);
    return;
  }
  const destination = clientLanding(AuthController.getCapabilities(), inPortfolio());
  if (destination !== '/home') { router.navigate(destination); return; }
  if (!requireRouteCapability('/home')) return;

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'home' });

  // /home is the Dashboard (translated from the demo). The chat-experiment prototype
  // that used to live at home-route.js was removed 2026-08-10 (dead code audit) —
  // never wired to any route, kept 3 web components (m5t-chat-prompt-area/-prompt/
  // -response) alive with nothing real using them.
  import('./routes/dashboard-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'home' });
    })
    .catch(err => {
      console.error('[Router] Error loading home route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'home', error: err });
    });
});

router.on('/signout', () => {
  console.log('[Router] Route: /signout');

  // Check authentication (will convert to signout_route internally)
  if (!checkRouteAccess('signout')) {
    navigateToRoute('signin');
    return;
  }

  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'signout' });

  import('./routes/signout-route.js')
    .then(module => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'signout' });
    })
    .catch(err => {
      console.error('[Router] Error loading signout route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'signout', error: err });
    });
});

// ============================================
// Section Routes (persona-aware side-nav destinations)
// ============================================
// All sections require the same auth level as /home, so we gate them behind the
// known-good 'home' route token — mvvLegit's allow-list is CDN-configured and does
// not know per-section tokens. Capability/persona VISIBILITY is enforced in the nav
// (Step 5) and on the server (row-level scope); this guard is just authn.
const SECTION_ROUTES = [
  // NOTE: /magnets has a dedicated handler below (real view), so it is intentionally NOT
  // in this placeholder list — same reason /clients and /team aren't.
  // NOTE: /templates has a dedicated handler below (real view — the starter-flow
  // gallery), so it is intentionally NOT in this placeholder list.
  // NOTE: /conversations and /ab-testing have dedicated handlers below (real views).
  // NOTE: /analytics has a dedicated handler below (real view — the per-magnet
  // overview), so it is intentionally NOT in this placeholder list.
  // NOTE: /ad-accounts has a dedicated handler below (real view — connection
  // status + associated Customer IDs), so it is intentionally NOT in this list.
  // Navigo resolves the FIRST registered match, so a placeholder entry here would
  // permanently shadow that handler no matter where it is registered.
  // NOTE: /clients and /team have dedicated handlers below (real views), so they are
  // intentionally NOT in this placeholder list.
  // NOTE: /billing has a dedicated handler below (real view — plan + usage meters +
  // Stripe surface), so it is intentionally NOT in this placeholder list.
  // NOTE: /settings (General) has a dedicated handler below (real view — profile
  // + preferences), so it is intentionally NOT in this placeholder list.
  // Sub-nav children (parent routes above cover Overview/General/All which share the parent path)
  { path: '/magnets/drafts',          name: 'magnets/drafts',          title: 'Magnets · Drafts',        icon: 'widgets' },
  { path: '/magnets/archived',        name: 'magnets/archived',        title: 'Magnets · Archived',      icon: 'widgets' },
  // NOTE: /analytics/conversions and /analytics/ad-performance both have dedicated
  // handlers below (real views), so they are intentionally NOT in this placeholder list.
  // Navigo resolves the FIRST registered match, and this list is registered first, so
  // leaving the placeholder here would permanently shadow the real page.
  // NOTE: /settings/workspace, /settings/integrations and /settings/security all
  // have dedicated handlers below (real views), so they are intentionally NOT in
  // this placeholder list.
];

SECTION_ROUTES.forEach(({ path, name, title, icon }) => {
  router.on(path, () => {
    console.log(`[Router] Route: ${path}`);
    if (!checkRouteAccess('home')) {
      navigateToRoute('signin');
      return;
    }
    if (!requireRouteCapability(path)) return;
    PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: name });
    import('./routes/section-route.js')
      .then((module) => {
        const container = document.getElementById('app');
        container.innerHTML = module.render({ title, icon, name });
        if (module.init) module.init({ name });
        PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: name });
      })
      .catch((err) => {
        console.error(`[Router] Error loading section route (${name}):`, err);
        PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: name, error: err });
      });
  });
});

// Settings → General — real view: the caller's own profile + preferences
// (personal tab: no portfolio picker, no org scoping).
router.on('/settings', () => {
  console.log('[Router] Route: /settings');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings' });
  import('./routes/settings-general-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings' });
    })
    .catch((err) => {
      console.error('[Router] Error loading settings-general route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings', error: err });
    });
});

// Settings → Workspace — real view: the org page (identity + team summary).
// Org-level tab like /team: no portfolio picker; reseller-in-client-context
// edits that client org.
router.on('/settings/workspace', () => {
  console.log('[Router] Route: /settings/workspace');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings/workspace')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/workspace' });
  import('./routes/settings-workspace-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/workspace' });
    })
    .catch((err) => {
      console.error('[Router] Error loading settings-workspace route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings/workspace', error: err });
    });
});

// Settings → Notifications — real view: the central lead-alert engine (per-magnet
// alert recipients + send-test).
router.on('/settings/notifications', () => {
  console.log('[Router] Route: /settings/notifications');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings/notifications')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/notifications' });
  import('./routes/settings-notifications-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/notifications' });
    })
    .catch((err) => {
      console.error('[Router] Error loading settings-notifications route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings/notifications', error: err });
    });
});

// Shopify opens this confirmation in Magnet; no platform tokens leave this app.
router.on('/shopify/connect', () => {
  if (!checkRouteAccess('home')) {
    // Preserve validated connection intent through sign-in/signup; never Shopify credentials.
    rememberShopifyBootRoute(window.location.hash);
    navigateToRoute('signin');
    return;
  }
  clearShopifyBootRoute();
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/integrations' });
  import('./routes/shopify-connect-route.js').then(module => {
    if (!window.location.hash.startsWith('#/shopify/connect')) return;
    document.getElementById('app').innerHTML = module.render();
    module.init();
    PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/integrations' });
  });
});

// Settings → Integrations — the HUB: provider tiles that navigate into
// per-provider detail pages. Each tile's availability follows its provider's
// server-side isConfigured() check, not a roadmap gate here.
router.on('/settings/integrations', () => {
  console.log('[Router] Route: /settings/integrations');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings/integrations')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/integrations' });
  import('./routes/settings-integrations-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/integrations' });
    })
    .catch((err) => {
      console.error('[Router] Error loading settings-integrations route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings/integrations', error: err });
    });
});

// Settings → Integrations → one provider's detail page. The app's first
// parameterized route: Navigo hands the segment via match.data.provider.
// DELIBERATE: ROUTE_NAV_BEFORE/AFTER publish the PARENT route name
// ('settings/integrations') — sideNavRouteIndex is keyed on route strings, so
// publishing the parameterized path would clear the sub-nav and the left-nav
// active state. Slug validation lives in the module (unknown → bounce to hub).
router.on('/settings/integrations/:provider', (match) => {
  const provider = match?.data?.provider || '';
  console.log('[Router] Route: /settings/integrations/' + provider);
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings/integrations/:provider')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/integrations' });
  (provider === 'shopify' ? import('./routes/shopify-management-route.js') : import('./routes/settings-integration-detail-route.js'))
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render({ provider });
      if (module.init) module.init({ provider });
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/integrations' });
    })
    .catch((err) => {
      console.error('[Router] Error loading integration detail route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings/integrations', error: err });
    });
});

// Settings → Security — real view: manage the caller's own passkeys (WebAuthn).
// Personal to the signed-in account, not org/tenancy-scoped — no portfolio/client
// picker, unlike Notifications/Integrations.
router.on('/settings/security', () => {
  console.log('[Router] Route: /settings/security');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/settings/security')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'settings/security' });
  import('./routes/settings-security-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'settings/security' });
    })
    .catch((err) => {
      console.error('[Router] Error loading settings-security route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'settings/security', error: err });
    });
});

// Conversations — real view: magnet sessions/journeys (completed, in progress, abandoned)
// from the funnel events, with completed sessions joined to their lead.
router.on('/conversations', () => {
  console.log('[Router] Route: /conversations');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/conversations')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'conversations' });
  import('./routes/conversations-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'conversations' });
    })
    .catch((err) => {
      console.error('[Router] Error loading conversations route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'conversations', error: err });
    });
});

// A/B Testing — real view (per-variant funnel comparisons + Declare Winner). Same authn
// gate as the sections; nav visibility is capability-gated (manage_magnets) + server-enforced.
router.on('/ab-testing', () => {
  console.log('[Router] Route: /ab-testing');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/ab-testing')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'ab-testing' });
  import('./routes/ab-testing-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'ab-testing' });
    })
    .catch((err) => {
      console.error('[Router] Error loading ab-testing route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'ab-testing', error: err });
    });
});

// Magnets — real view, not the generic placeholder. Same authn gate as the sections;
// capability visibility is enforced by the nav + the server (POST /magnets requires
// PROVISION_MAGNETS, scoped to the caller's owner orgs).
router.on('/magnets', () => {
  console.log('[Router] Route: /magnets');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/magnets')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'magnets' });
  (magnetOverviewEnabled() ? import('./routes/magnet-overview-route.js') : import('./routes/magnets-route.js'))
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'magnets' });
    })
    .catch((err) => {
      console.error('[Router] Error loading magnets route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'magnets', error: err });
    });
});

registerMagnetWorkspaceRoutes(router, { checkRouteAccess });
registerMagnetOverviewRoutes(router, { checkRouteAccess });

// Leads — the captured leads for the caller's magnets (tenant-scoped m5mLead).
// Templates — real view: browse the starter flows with their descriptions. Picking one
// hands off to /magnets, which owns the provisioning path (see templates-route.js).
// PUBLIC on purpose (no checkRouteAccess): the gallery renders from the public
// /templates catalog and holds no tenant data, so a signed-out visitor may browse it
// (the website links here). The commit action is what changes shape: signed out, Use
// parks the choice and routes to #/signup instead of #/magnets — see templates-route.js.
router.on('/templates', () => {
  console.log('[Router] Route: /templates');
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'templates' });
  import('./routes/templates-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'templates' });
    })
    .catch((err) => {
      console.error('[Router] Error loading templates route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'templates', error: err });
    });
});

// Questionnaires — real view: kickoff questionnaire status across every client, with a
// drawer to read a submitted one. Reseller-facing; the nav gates it on
// create_client_accounts and the API owner-scopes the data.
router.on('/questionnaires', () => {
  console.log('[Router] Route: /questionnaires');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/questionnaires')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'questionnaires' });
  import('./routes/questionnaires-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'questionnaires' });
    })
    .catch((err) => {
      console.error('[Router] Error loading questionnaires route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'questionnaires', error: err });
    });
});

// Deliverables — client-facing plans (Advertising Plan first): author, send for
// review, and track sign-off status. Reseller-facing; the nav gates it on
// create_client_accounts and the API owner-scopes the data.
router.on('/deliverables', () => {
  console.log('[Router] Route: /deliverables');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/deliverables')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'deliverables' });
  // One nav entry, two personas: resellers/admins manage deliverables, a
  // managed client reviews the plans sent to them. LITERAL import() strings
  // in each branch — same Vite constraint loadSplash documents.
  const mode = AuthController.getUiMode?.();
  const loader = (mode === 'reseller' || mode === 'admin')
    ? import('./routes/deliverables-route.js')
    : import('./routes/deliverables-client-route.js');
  loader
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'deliverables' });
    })
    .catch((err) => {
      console.error('[Router] Error loading deliverables route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'deliverables', error: err });
    });
});

router.on('/leads', () => {
  console.log('[Router] Route: /leads');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/leads')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'leads' });
  import('./routes/leads-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'leads' });
    })
    .catch((err) => {
      console.error('[Router] Error loading leads route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'leads', error: err });
    });
});

// Analytics · Overview — per-magnet performance. Was a SECTION_ROUTES placeholder, so
// the nav advertised it and it landed on a stub. Reads dashboard/stats?group_by=magnet;
// the server owner-scopes the rows.
router.on('/performance', async () => {
  if (!checkRouteAccess('home')) { navigateToRoute('signin'); return; }
  if (!requireRouteCapability('/performance')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'performance' });
  try {
    const module = await import('./routes/performance-route.js');
    if (window.location.hash !== '#/performance') return;
    document.getElementById('app').innerHTML = module.render();
    PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'performance' });
    await module.init();
  } catch (error) {
    PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'performance', error });
  }
});

router.on('/analytics', () => {
  console.log('[Router] Route: /analytics');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/analytics')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'analytics' });
  import('./routes/analytics-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'analytics' });
    })
    .catch((err) => {
      console.error('[Router] Error loading analytics route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'analytics', error: err });
    });
});

// Conversions — the modular conversions report: pick which Google Ads conversion actions
// count and every derived figure recomputes against that selection (owner-scoped
// coreAdMetrics/conversions + upload-ledger). Gated by view_ad_metrics in the nav; saving
// a default selection is separately MANAGE_MAGNETS-gated server-side.
router.on('/analytics/conversions', () => {
  console.log('[Router] Route: /analytics/conversions');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/analytics/conversions')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'analytics/conversions' });
  import('./routes/analytics-conversions-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'analytics/conversions' });
    })
    .catch((err) => {
      console.error('[Router] Error loading conversions route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'analytics/conversions', error: err });
    });
});

// Billing — plan + usage meters (MEASURE slice; view_billing in the nav, the
// server owner-scopes the data and gates on the same capability).
// SELF-SERVE ONLY: agency-side orgs are billed through their program and see
// no Billing surface at all — the nav hides it (uiModes) and this guard backs
// the nav up for direct URLs.
router.on('/billing', () => {
  console.log('[Router] Route: /billing');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (AuthController.getUiMode?.() !== 'self_serve') {
    window.location.hash = '#/home';
    return;
  }
  // Runs AFTER the uiMode gate above, and the order is deliberate: uiMode asks whether a
  // Billing surface EXISTS for this org, capability asks whether this person may see one
  // that does. Neither subsumes the other — a saas_org member is self_serve without
  // view_billing (only this stops them), a reseller owner holds view_billing without
  // being self_serve (only the gate above stops them).
  //
  // TWO KNOWN ROUGH EDGES, deliberately not fixed here (separate commit, after merge):
  // the uiMode branch denies SILENTLY via a raw hash assignment that bypasses the router,
  // while this one toasts — same user action, two experiences, and the silent one covers
  // the larger population. And it hardcodes 'self_serve' while the nav entry already
  // declares uiModes: ["self_serve"], so the rule lives in two places.
  if (!requireRouteCapability('/billing')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'billing' });
  import('./routes/billing-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'billing' });
    })
    .catch((err) => {
      console.error('[Router] Error loading billing route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'billing', error: err });
    });
});

// Traffic — GA4 website traffic correlated to magnet leads (owner-scoped
// coreAnalyticsMetrics + m5mLead overlay). Gated by view_stats in the nav;
// the server owner-scopes the data.
router.on('/analytics/traffic', () => {
  console.log('[Router] Route: /analytics/traffic');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/analytics/traffic')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'analytics/traffic' });
  import('./routes/analytics-traffic-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'analytics/traffic' });
    })
    .catch((err) => {
      console.error('[Router] Error loading traffic route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'analytics/traffic', error: err });
    });
});

// Ad Performance — managed-client analytics: ad spend correlated to magnet leads
// (owner-scoped coreAdMetrics + m5mLead overlay). Gated by view_ad_metrics in the nav;
// the server owner-scopes the data.
router.on('/analytics/ad-performance', () => {
  console.log('[Router] Route: /analytics/ad-performance');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/analytics/ad-performance')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'analytics/ad-performance' });
  import('./routes/ad-performance-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'analytics/ad-performance' });
    })
    .catch((err) => {
      console.error('[Router] Error loading ad-performance route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'analytics/ad-performance', error: err });
    });
});

// Ad Accounts (Advertising section) — real view: the org's Google Ads connection
// status (shared derivation with Settings → Integrations) plus the coreAdAccount
// Customer ID associations feeding reporting. Management links out to the
// settings detail page and the Clients page rather than duplicating those flows.
router.on('/ad-accounts', () => {
  console.log('[Router] Route: /ad-accounts');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/ad-accounts')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'ad-accounts' });
  import('./routes/ad-accounts-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'ad-accounts' });
    })
    .catch((err) => {
      console.error('[Router] Error loading ad-accounts route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'ad-accounts', error: err });
    });
});

// Clients (reseller persona) — real view, not the generic placeholder. Same authn
// gate as the sections; capability/persona visibility is enforced by the nav + the
// server (POST /acctGroup requires CREATE_CLIENT_ACCOUNTS).
router.on('/clients', () => {
  console.log('[Router] Route: /clients');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/clients')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'clients' });
  import('./routes/clients-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'clients' });
    })
    .catch((err) => {
      console.error('[Router] Error loading clients route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'clients', error: err });
    });
});

// Team (own-org member management) — real view, not the generic placeholder. Capability
// (manage_team) visibility is enforced by the nav + the server.
router.on('/team', () => {
  console.log('[Router] Route: /team');
  if (!checkRouteAccess('home')) {
    navigateToRoute('signin');
    return;
  }
  if (!requireRouteCapability('/team')) return;
  PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'team' });
  import('./routes/team-route.js')
    .then((module) => {
      const container = document.getElementById('app');
      container.innerHTML = module.render();
      if (module.init) module.init();
      PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'team' });
    })
    .catch((err) => {
      console.error('[Router] Error loading team route:', err);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'team', error: err });
    });
});

router.notFound(() => {
  console.log('[Router] Route not found - redirecting to splash');
  navigateToRoute('splash');
});

// ============================================
// PubSub Navigation Handler
// ============================================

// Subscribe to ROUTE_NAV events and trigger navigation
PubSub.subscribe(AppBroadcast.ROUTE_NAV, (_msg, data) => {
  console.log('[Router] ROUTE_NAV event received:', data);
  if (data?.route) {
    navigateToRoute(data.route);
  }
});

// ============================================
// Initialize Application
// ============================================

// Wait for Neodigm 55 library to load, then initialize
// Note: We need to wait for both DOM and external scripts
function waitForNeodigm() {
  console.log('[App] Waiting for Neodigm 55 library...');

  let attempts = 0;
  const maxAttempts = 100; // 10 seconds (100 * 100ms)

  const checkMvvLegit = setInterval(() => {
    attempts++;

    // Log debug info every 10 attempts (1 second)
    if (attempts % 10 === 0) {
      console.log(`[App] Still waiting... attempt ${attempts}/100`, {
        mvvLegitExists: typeof mvvLegit !== 'undefined',
        neodigmLoaded: window.__neodigmLoaded
      });
    }

    if (typeof mvvLegit !== 'undefined') {
      console.log(`[App] Neodigm 55 loaded after ${attempts * 100}ms`);
      clearInterval(checkMvvLegit);
      initializeApp();
    } else if (attempts >= maxAttempts) {
      clearInterval(checkMvvLegit);
      console.error('[App] Failed to load Neodigm 55 library after 10 seconds');
      console.error('[App] Check browser console for CORS or network errors');

      // Show error in UI
      document.getElementById('app').innerHTML = `
        <div style="padding: 2rem; text-align: center; font-family: sans-serif;">
          <h2 style="color: #f44336;">Failed to Load Authentication Library</h2>
          <p>The Neodigm 55 library could not be loaded from the CDN.</p>
          <p>Please check your internet connection and try refreshing the page.</p>
          <button onclick="location.reload()" style="padding: 10px 20px; font-size: 1rem; cursor: pointer;">
            Refresh Page
          </button>
        </div>
      `;

      PubSub.publish(AppBroadcast.APP_ERROR, {
        message: 'Failed to load authentication library'
      });
    }
  }, 100);
}

// Start waiting as soon as possible
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForNeodigm);
} else {
  // DOM already loaded
  waitForNeodigm();
}

/**
 * Initialize application after Neodigm 55 is loaded
 */
async function initializeApp() {
  console.log('[App] Neodigm 55 loaded, initializing...');

  // Restore the visitor's persisted theme choice (always light on first visit) and
  // animation preference as early as possible — before anything else renders.
  initTheme();
  applyAnimationsPref();

  // Initialize event system
  initAppEvents();

  // Note: MachVive SSE to PubSub bridge is initialized automatically by mvvSSEther
  // mvvSSEther detects MachVive and calls MachVive.init() when snippet loads
  // mvvSSEther auto-connects 16 seconds after page load

  // Initialize i18n Controller (must be before route navigation)
  I18nController.initialize();
  console.log('[App] I18n Controller initialized');

  // Initialize AuthController (replaces direct mvvLegit initialization)
  const authInitialized = await AuthController.init({
    // RULE: All client apps point to hosted API (no localhost fallback)
    BASE_URL: 'https://machfivemagnet-saas.onrender.com',
    navigateFunction: (routePath) => {
      console.log('[AuthController] Navigation requested:', routePath);
      navigateToRoute(routePath);
    }
  });

  if (!authInitialized) {
    console.error('[App] AuthController initialization failed');
    return;
  }

  // Note: AuthController already handles mvvLegit state changes internally
  // The APP_AUTH event will be published by AuthController's state callback if needed

  // Load persisted session user from localStorage (AuthController handles this)
  const sessionUser = AuthController.getEntity();
  if (sessionUser) {
    console.log('[App] Session restored for:', sessionUser.email);
    // Publish entity loaded event for other components to use
    PubSub.publish(AppBroadcast.APP_USER_LOADED, { entity: sessionUser });
    // Reconcile tenancy scope against the server — only when actually authenticated
    // (a cached entity may be UNVERF/expired with no token).
    if (AuthController.isAuthenticated()) {
      // REGRESSION GUARD (sign-out-on-refresh): a stored ACTIVE_ORG races every
      // other tenancy-scoped fetch this boot fires right after (router.resolve()
      // below → the landing route's own init(), the notification bell, etc.).
      // attachScope (service-api's middleware/tenancyScope.js) hard-403s with
      // code 'invalid_active_org' when that org turns out to be stale/
      // inaccessible — deliberately: "fail closed on a bad org — silently
      // falling back would blend tenants." mvvLegit's own global fetch
      // interceptor can't tell that apart from a dead token: it treats ANY
      // 401/403 from ANY endpoint as "the whole session is invalid" and
      // unconditionally signs out. fetchScope()'s own /me call is what
      // detects and clears a stale ACTIVE_ORG — normally fire-and-forget so
      // routing isn't delayed, but that's exactly the open window: if the
      // landing route's own fetch (carrying the same stale header) lands
      // before this cleanup does, ITS legitimate scope-403 nukes a session
      // whose Bearer token was perfectly valid the whole time.
      //
      // Only await when there's actually an override to race against — a
      // single-org user with no ACTIVE_ORG override has nothing to
      // reconcile, so the common case keeps its non-blocking, cache-first
      // paint unchanged.
      if (localStorage.getItem(AuthController.CONFIG.LS_KEYS.ACTIVE_ORG)) {
        await AuthController.fetchScope();
      } else {
        AuthController.fetchScope();
      }
    }
  }

  // Publish app ready event
  PubSub.publish(AppBroadcast.APP_READY, {
    timestamp: new Date().toISOString(),
    sessionUser
  });

  // Feed + wire the persona-aware side nav before resolving the first route.
  setupSideNav();
  mountNotificationBell();

  // Resolve routes
  router.resolve();

  if (import.meta.env.DEV && new URLSearchParams(location.search).has('editor-recovery-check')) {
    import('./dev/editor-recovery-check.js').then(module => module.mountEditorRecoveryCheck());
  }
  if (import.meta.env.DEV && new URLSearchParams(location.search).has('editor-save-check')) {
    import('./dev/editor-save-check.js').then(module => module.mountEditorSaveCheck());
  }

  // Expose controllers globally for debugging and language switching
  window.AuthController = AuthController;
  window.I18nController = I18nController;
  window.MachVive = MachVive;
  console.log('[App] StreamSyncEngage client initialized (AuthController, I18nController, MachVive available globally)');
}

// Subscribe to route navigation events to trigger i18n refresh
PubSub.subscribe(AppBroadcast.ROUTE_NAV_AFTER, (_msg, data) => {
  // Trigger i18n refresh after route navigation completes
  if (typeof I18nController !== 'undefined' && I18nController.refresh) {
    setTimeout(() => {
      I18nController.refresh();
    }, 100); // Small delay to ensure DOM is updated
  }
});
